import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/UI/Button';
import { ConfirmModal } from '../../../components/UI/ConfirmModal';
import { useToast } from '../../../components/UI/ToastProvider';
import { productModuleService } from '../../../services/productModuleService';
import type { ProductModule } from '../../../types/productModule';

type FormState = {
  id?: string;
  code: string;
  name: string;
  description: string;
  defaultDashboardUrl: string;
  active: boolean;
  sortOrder: number;
};

const initialFormState: FormState = {
  code: '',
  name: '',
  description: '',
  defaultDashboardUrl: '/',
  active: true,
  sortOrder: 0,
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: { message?: string; error?: string; title?: string } } })?.response?.data;
  if (response?.error)
  {
    return response.error;
  }
  if (response?.message)
  {
    return response.message;
  }
  if (response?.title)
  {
    return response.title;
  }
  return fallback;
};

export const ProductModulesPage: React.FC = () => {
  const { addToast } = useToast();
  const [modules, setModules] = useState<ProductModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; item: ProductModule | null }>({
    open: false,
    item: null,
  });

  const loadModules = async () => {
    try
    {
      setLoading(true);
      const data = await productModuleService.getAll();
      setModules(data);
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao carregar módulos de produto.'), 'error');
    }
    finally
    {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadModules();
  }, []);

  const filteredModules = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term)
    {
      return modules;
    }
    return modules.filter(
      (item) =>
        item.code.toLowerCase().includes(term) ||
        item.name.toLowerCase().includes(term) ||
        item.defaultDashboardUrl.toLowerCase().includes(term)
    );
  }, [modules, search]);

  const openCreate = () => {
    setFormState(initialFormState);
    setShowForm(true);
  };

  const openEdit = (item: ProductModule) => {
    setFormState({
      id: item.id,
      code: item.code,
      name: item.name,
      description: item.description ?? '',
      defaultDashboardUrl: item.defaultDashboardUrl,
      active: item.active,
      sortOrder: item.sortOrder,
    });
    setShowForm(true);
  };

  const validateForm = (): boolean => {
    if (!formState.code.trim())
    {
      addToast('Informe o código do módulo.', 'error');
      return false;
    }
    if (formState.code.trim() !== formState.code.trim().toUpperCase())
    {
      addToast('O código deve estar em maiúsculas.', 'error');
      return false;
    }
    if (!formState.name.trim())
    {
      addToast('Informe o nome do módulo.', 'error');
      return false;
    }
    if (!formState.defaultDashboardUrl.startsWith('/'))
    {
      addToast('A URL padrão deve começar com "/".', 'error');
      return false;
    }
    return true;
  };

  const onSave = async () => {
    if (!validateForm())
    {
      return;
    }

    setSaving(true);
    try
    {
      const payload = {
        code: formState.code.trim().toUpperCase(),
        name: formState.name.trim(),
        description: formState.description.trim() || null,
        defaultDashboardUrl: formState.defaultDashboardUrl.trim(),
        active: formState.active,
        sortOrder: formState.sortOrder,
      };

      if (formState.id)
      {
        await productModuleService.update(formState.id, { id: formState.id, ...payload });
        addToast('Módulo atualizado com sucesso.', 'success');
      }
      else
      {
        await productModuleService.create(payload);
        addToast('Módulo criado com sucesso.', 'success');
      }

      setShowForm(false);
      await loadModules();
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Não foi possível salvar o módulo.'), 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  const handleDelete = (item: ProductModule) => {
    setConfirmDelete({ open: true, item });
  };

  const confirmDeleteModule = async () => {
    if (!confirmDelete.item)
    {
      return;
    }

    try
    {
      await productModuleService.delete(confirmDelete.item.id);
      addToast('Módulo excluído com sucesso.', 'success');
      await loadModules();
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Não foi possível excluir o módulo.'), 'error');
    }
    finally
    {
      setConfirmDelete({ open: false, item: null });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Módulos de Produto</h1>
          <p className="text-sm text-gray-600">
            Catálogo de módulos comerciais (Know, Btalk, etc.) e URLs padrão de dashboard.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Novo módulo
        </button>
      </div>

      <div className="max-w-md">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por código, nome ou URL..."
          className="w-full rounded-md border border-gray-300 px-3 py-2"
        />
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500">Carregando...</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">URL padrão</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ordem</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredModules.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">{item.code}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">{item.defaultDashboardUrl}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.sortOrder}</td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        item.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {item.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => openEdit(item)} title="Editar">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(item)} title="Excluir">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredModules.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    Nenhum módulo de produto encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900">
              {formState.id ? 'Editar módulo' : 'Novo módulo'}
            </h2>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Código *</label>
                <input
                  type="text"
                  value={formState.code}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono"
                  maxLength={50}
                  placeholder="KNOW"
                />
                <p className="mt-1 text-xs text-gray-500">Use letras maiúsculas (ex.: KNOW, BTALK).</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nome *</label>
                <input
                  type="text"
                  value={formState.name}
                  onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  maxLength={100}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
                <textarea
                  value={formState.description}
                  onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  rows={2}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">URL padrão do dashboard *</label>
                <input
                  type="text"
                  value={formState.defaultDashboardUrl}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, defaultDashboardUrl: e.target.value }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono"
                  placeholder="/dashboard"
                />
                <p className="mt-1 text-xs text-gray-500">Deve começar com &quot;/&quot;.</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Ordem</label>
                <input
                  type="number"
                  value={formState.sortOrder}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      sortOrder: e.target.value === '' ? 0 : parseInt(e.target.value, 10),
                    }))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  min={0}
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formState.active}
                  onChange={(e) => setFormState((prev) => ({ ...prev, active: e.target.checked }))}
                />
                Módulo ativo
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={() => void onSave()}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                disabled={saving}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete.open}
        title="Confirmar exclusão"
        message={`Tem certeza que deseja excluir o módulo "${confirmDelete.item?.name}"? Esta ação não poderá ser desfeita.`}
        onConfirm={() => void confirmDeleteModule()}
        onCancel={() => setConfirmDelete({ open: false, item: null })}
        confirmText="Excluir"
        cancelText="Cancelar"
      />
    </div>
  );
};
